import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const BLANK = { title: "", role_type: "teacher", location: "", employment_type: "Full-time", schedule: "",
                summary: "", responsibilities: [], essential_duties: [], qualifications: [], credentials: [],
                pay_min: "", pay_max: "", pay_period: "", pay_published: false, deadline: "",
                openings: 1, questions: [] };
const LISTS = [["responsibilities", "Responsibilities"], ["essential_duties", "Essential duties"],
               ["qualifications", "Qualifications"], ["credentials", "Relevant credentials"]];

let keySeq = 0;
const newQuestion = () => { keySeq += 1; return { key: `new${keySeq}`, label: "", type: "short_text", required: false, options: [] }; };

export default function PositionDialog({ meta, position, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK, ...position,
    pay_min: position.pay_min ?? "", pay_max: position.pay_max ?? "",
    questions: (position.questions || []).map((q) => ({ ...q })) });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form,
        pay_min: form.pay_min === "" ? null : Number(form.pay_min),
        pay_max: form.pay_max === "" ? null : Number(form.pay_max),
        openings: Number(form.openings) || 1,
        questions: form.questions.filter((q) => q.label.trim())
          .map((q) => ({ ...q, key: String(q.key).startsWith("new") ? "" : q.key })) };
      ["id", "created_at", "created_by", "created_by_name", "updated_at", "updated_by", "history", "status",
       "posted_at", "closed_at", "accepting", "role_label", "application_count"].forEach((k) => delete payload[k]);
      if (position.id) await api.put(`/hiring/positions/${position.id}`, payload);
      else await api.post("/hiring/positions", payload);
      toast.success(position.id ? "Position updated" : "Position saved as a draft");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setList = (key) => (e) => setForm({ ...form, [key]: e.target.value.split("\n") });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="position-dialog">
        <DialogHeader><DialogTitle>{position.id ? "Edit position" : "Create a position"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="font-bold">Position title</Label>
              <Input className="mt-2" value={form.title} data-testid="position-title"
                     onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label className="font-bold">Position type</Label>
              <Select value={form.role_type} onValueChange={(v) => setForm({ ...form, role_type: v })}>
                <SelectTrigger className="mt-2" data-testid="position-role-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(meta.role_types).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-bold">Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                <SelectTrigger className="mt-2" data-testid="position-employment-type"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.employment_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-bold">Location</Label>
              <Input className="mt-2" value={form.location} data-testid="position-location"
                     onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div>
              <Label className="font-bold">Schedule</Label>
              <Input className="mt-2" value={form.schedule} placeholder="Mon–Fri, 7:30 AM – 4:30 PM"
                     data-testid="position-schedule" onChange={(e) => setForm({ ...form, schedule: e.target.value })} />
            </div>
            <div>
              <Label className="font-bold">Application deadline (optional)</Label>
              <Input type="date" className="mt-2" value={form.deadline} data-testid="position-deadline"
                     onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
            <div>
              <Label className="font-bold">Openings</Label>
              <Input type="number" min="1" className="mt-2" value={form.openings} data-testid="position-openings"
                     onChange={(e) => setForm({ ...form, openings: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-bold">Summary shown on the careers page</Label>
              <Textarea className="mt-2 min-h-24" value={form.summary} data-testid="position-summary"
                        onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {LISTS.map(([key, label]) => (
              <div key={key}>
                <Label className="font-bold">{label}</Label>
                <Textarea className="mt-2 min-h-28" value={(form[key] || []).join("\n")} placeholder="One per line"
                          data-testid={`position-${key}`} onChange={setList(key)} />
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-border p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-bold">Publish the pay range on the public listing</p>
                <p className="text-sm text-muted-foreground mt-1">Off by default. The range is only shown when you turn this on.</p>
              </div>
              <Switch checked={form.pay_published} data-testid="position-pay-published"
                      onCheckedChange={(v) => setForm({ ...form, pay_published: v })} />
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              <div><Label className="font-bold">From</Label>
                <Input type="number" step="0.01" className="mt-2" value={form.pay_min} data-testid="position-pay-min"
                       onChange={(e) => setForm({ ...form, pay_min: e.target.value })} /></div>
              <div><Label className="font-bold">To</Label>
                <Input type="number" step="0.01" className="mt-2" value={form.pay_max} data-testid="position-pay-max"
                       onChange={(e) => setForm({ ...form, pay_max: e.target.value })} /></div>
              <div><Label className="font-bold">Per</Label>
                <Select value={form.pay_period} onValueChange={(v) => setForm({ ...form, pay_period: v })}>
                  <SelectTrigger className="mt-2" data-testid="position-pay-period">
                    <SelectValue placeholder="Choose a period" />
                  </SelectTrigger>
                  <SelectContent>{meta.pay_periods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            {form.pay_published && (!form.pay_period || !(form.pay_min || form.pay_max)) && (
              <p className="mt-3 text-sm font-semibold text-[#EF4444]" data-testid="pay-period-warning">
                Enter the range and choose whether it is per hour, week, month or year before publishing it.
              </p>
            )}
          </div>

          <div>
            <p className="font-bold">Questions for this position</p>
            <p className="text-sm text-muted-foreground mt-1">
              Job-related questions only. Do not ask about diagnoses, family medical history, marital status,
              children, or date of birth.
            </p>
            <div className="mt-4 space-y-3" data-testid="position-questions-editor">
              {form.questions.map((q, i) => (
                <div key={q.key} className="rounded-xl border border-border p-4 grid gap-3 sm:grid-cols-[1fr,10rem,7rem,5rem]">
                  <Input value={q.label} placeholder="Question" data-testid={`question-label-${i}`}
                         onChange={(e) => setForm({ ...form, questions: form.questions.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                  <Select value={q.type} onValueChange={(v) => setForm({ ...form, questions: form.questions.map((x, j) => (j === i ? { ...x, type: v } : x)) })}>
                    <SelectTrigger data-testid={`question-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.question_types.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 text-sm font-semibold">
                    <Switch checked={q.required} data-testid={`question-required-${i}`}
                            onCheckedChange={(v) => setForm({ ...form, questions: form.questions.map((x, j) => (j === i ? { ...x, required: v } : x)) })} />
                    Required
                  </label>
                  <Button variant="link" className="text-[#EF4444] px-0" data-testid={`remove-question-${i}`}
                          onClick={() => setForm({ ...form, questions: form.questions.filter((_, j) => j !== i) })}>Remove</Button>
                  {q.type === "choice" && (
                    <Input className="sm:col-span-4" value={(q.options || []).join(", ")} placeholder="Choices, separated by commas"
                           data-testid={`question-options-${i}`}
                           onChange={(e) => setForm({ ...form, questions: form.questions.map((x, j) => (j === i ? { ...x, options: e.target.value.split(",").map((o) => o.trim()) } : x)) })} />
                  )}
                </div>
              ))}
              <Button variant="outline" className="rounded-full font-bold" data-testid="add-question-btn"
                      onClick={() => setForm({ ...form, questions: [...form.questions, newQuestion()] })}>Add a question</Button>
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={save} disabled={busy} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="save-position-btn">{busy ? "Saving…" : "Save position"}</Button>
            <Button variant="outline" onClick={onClose} className="rounded-full font-bold">Cancel</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Saving keeps the posting in its current state. A draft is never visible to the public until you publish it.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
